import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { supabase } from "@/lib/customSupabaseClient";
import { usePresaleAccess } from "@/hooks/usePresaleAccess";

// ─── Brand colors ───────────────────────────────────
// Founding Member is parked (migration 20260916_004, is_active = false) until the brick split
// and the membership clock are ruled. Hide the card so nobody clicks into "not on the menu yet".
// Flip to true in the same commit that unparks the tier.
const FOUNDING_MEMBER_ON_MENU = false;

const C = {
  blue: "var(--blue)",
  gold: "var(--gold)",
  goldHover: "var(--gold-hover)",
  cream: "var(--cream)",
  dark: "var(--charcoal)",
  soft: "color-mix(in srgb, var(--charcoal) 88%, transparent)",
  gray: "color-mix(in srgb, var(--navy) 55%, transparent)",
  divider: "color-mix(in srgb, var(--navy) 12%, transparent)",
};

// ─── Toast component ────────────────────────────────
// This is a small notification that slides up from the bottom
// when someone clicks a tier. It shows for 4 seconds, then fades out.
// No external library needed — just useState + setTimeout.
function Toast({ message, visible }: { message: string; visible: boolean }) {
  return (
    <div
      style={{
        position: "fixed",
        bottom: visible ? 32 : -100,
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: C.dark,
        color: C.cream,
        padding: "16px 28px",
        borderRadius: 8,
        fontFamily: 'var(--sans)',
        fontSize: 14,
        lineHeight: 1.5,
        zIndex: 9999,
        transition: "bottom 0.3s ease",
        maxWidth: 400,
        textAlign: "center",
        boxShadow: "0 4px 20px color-mix(in srgb, var(--charcoal) 30%, transparent)",
      }}
    >
      {message}
    </div>
  );
}

// ─── Main page ──────────────────────────────────────
export default function BuildPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [brickChoice, setBrickChoice] = useState<string | null>(null);
  const [toast, setToast] = useState({ visible: false, message: "" });

  const navigate = useNavigate();
  const { canCheckout } = usePresaleAccess();

  // ─── Intent click handler ───────────────────────
  // When someone clicks a tier card, we:
  // 1. Log it to Supabase (anonymous — no email attached)
  // 2. Show a toast nudging them to the email signup
  //
  // The .insert() call fires and we don't await it — 
  // we don't want the UI to freeze if Supabase is slow.
  // This is called "fire and forget." Fine for analytics.
  const logIntent = (tierId: string) => {
    // Fire and forget — don't block the UI
    (supabase
      .schema('public')
      .from("presale_intent_clicks") as any)
      .insert({ tier_id: tierId })
      .then(({ error }) => {
        if (error) console.error("Intent tracking error:", error);
      });

    setToast({
      visible: true,
      message: "Presale coming soon — leave your email below to be first in.",
    });
  };

  // Who can actually buy from this page: everyone once PRESALE_VISIBLE is true,
  // until then admins and presale_tester accounts (usePresaleAccess). Everyone
  // else gets the intent log + "coming soon" toast. Returns true when it navigated.
  const chooseTier = (tierId: string): boolean => {
    if (canCheckout) {
      navigate(`/build/checkout/${tierId}`);
      return true;
    }
    logIntent(tierId);
    return false;
  };

  // Auto-hide toast after 4 seconds
  useEffect(() => {
    if (!toast.visible) return;
    const timer = setTimeout(() => setToast((t) => ({ ...t, visible: false })), 4000);
    return () => clearTimeout(timer);
  }, [toast.visible]);

  // ─── Email submit handler ─────────────────────────
  // Basic email validation, then route through the join_waitlist RPC — the one
  // write path for all signups. The RPC is idempotent, so an already-joined
  // email just succeeds (no duplicate-error case to handle).
  const handleSubmit = async () => {
    if (!email) return;

    // Simple email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("That doesn't look like a valid email.");
      return;
    }

    setSubmitting(true);
    setEmailError("");

    const { error } = await supabase
      .rpc('join_waitlist', {
        p_email: email.toLowerCase().trim(),
        p_name: null,
        p_source: 'build',
      });

    setSubmitting(false);

    if (error) {
      console.error("Waitlist error:", error);
      setEmailError("Something went wrong. Try again?");
      return;
    }

    setSubmitted(true);
  };

  // ─── Reusable UI pieces ───────────────────────────
  const sectionLabel = (text: string) => (
    <p
      style={{
        fontFamily: 'var(--sans)',
        fontSize: 12,
        letterSpacing: 4,
        textTransform: "uppercase",
        color: C.gray,
        marginBottom: 24,
      }}
    >
      {text}
    </p>
  );

  // tierCard now takes a tierId and fires logIntent on click.
  // The "highlight" prop uses gold borders (for bricks).
  const tierCard = ({
    tierId,
    title,
    price,
    sub,
    desc,
    highlight,
    selected,
  }: {
    tierId: string;
    title: string;
    price: string;
    sub?: string;
    desc: string;
    highlight?: boolean;
    selected?: boolean;
  }) => (
    <div
      onClick={() => {
        if (chooseTier(tierId)) return;
        if (highlight) setBrickChoice(tierId);
      }}
      style={{
        border: selected
          ? `2px solid ${highlight ? C.gold : C.blue}`
          : `1px solid ${C.divider}`,
        borderRadius: 8,
        padding: 24,
        cursor: "pointer",
        backgroundColor: selected
          ? (highlight ? "color-mix(in srgb, var(--gold) 3%, transparent)" : "color-mix(in srgb, var(--blue) 3%, transparent)")
          : "white",
        transition: "all 0.2s ease",
      }}
    >
      <p
        style={{
          fontFamily: 'var(--sans)',
          fontSize: 12,
          letterSpacing: 1.5,
          textTransform: "uppercase",
          color: C.gray,
          marginBottom: 8,
        }}
      >
        {title}
      </p>
      <p
        style={{
          fontSize: 36,
          fontWeight: 600,
          color: C.dark,
          marginBottom: 4,
          fontFamily: 'var(--serif)',
        }}
      >
        {price}
      </p>
      {sub && (
        <p
          style={{
            fontFamily: 'var(--sans)',
            fontSize: 12,
            color: C.blue,
            marginBottom: 12,
          }}
        >
          {sub}
        </p>
      )}
      <p
        style={{
          fontFamily: 'var(--sans)',
          fontSize: 14,
          color: C.gray,
          lineHeight: 1.6,
        }}
      >
        {desc}
      </p>
    </div>
  );

  // ─── Page layout ──────────────────────────────────
  return (
    <>
      <Helmet>
        <title>Reserve Your Place — Maslow Private Restrooms in SoHo</title>
        <meta
          name="description"
          content="Reserve your visits at Maslow — New York's first real restroom, opening in SoHo. Pre-purchase passes and Maslow Bricks; use them the day we open."
        />
      </Helmet>

      <div
        style={{
          backgroundColor: C.cream,
          color: C.dark,
          fontFamily: 'var(--serif)',
          minHeight: "100vh",
        }}
      >
        {/* ===== HERO ===== */}
        <section
          style={{
            minHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "80px 24px 60px",
            maxWidth: 780,
            margin: "0 auto",
          }}
        >
          <p
            style={{
              fontFamily: 'var(--sans)',
              fontSize: 12,
              letterSpacing: 4,
              textTransform: "uppercase",
              color: C.gray,
              marginBottom: 40,
            }}
          >
            MASLOW
          </p>
          <h1
            style={{
              fontSize: "clamp(34px, 7vw, 60px)",
              fontWeight: 400,
              lineHeight: 1.1,
              marginBottom: 32,
            }}
          >
            New York's first real restroom.{" "}
            <span style={{ color: C.blue }}>Reserve your place.</span>
          </h1>
          <p
            style={{
              fontSize: "clamp(17px, 3vw, 21px)",
              lineHeight: 1.65,
              color: C.soft,
              maxWidth: 580,
              marginBottom: 48,
            }}
          >
            Twelve private rooms in SoHo — a locked door, warm light, and ten
            minutes that belong to you. Reserve your visits now; use them the
            day we open.
          </p>
          <div
            style={{
              padding: "18px 22px",
              backgroundColor: "color-mix(in srgb, var(--blue) 4%, transparent)",
              borderLeft: `3px solid ${C.blue}`,
              maxWidth: 500,
              marginBottom: 48,
            }}
          >
            <p
              style={{
                fontSize: 15,
                fontFamily: 'var(--sans)',
                lineHeight: 1.65,
                color: C.soft,
                margin: 0,
              }}
            >
              Last month,{" "}
              <strong style={{ color: C.blue }}>489 people</strong> searched
              Google for a public restroom in SoHo. They found us.
            </p>
          </div>
          <div
            style={{
              width: 1,
              height: 60,
              backgroundColor: C.divider,
              margin: "0 auto",
            }}
          />
        </section>

        {/* ===== THE PROBLEM ===== */}
        <section
          style={{
            padding: "60px 24px 80px",
            maxWidth: 680,
            margin: "0 auto",
          }}
        >
          {sectionLabel("The Problem")}
          <p style={{ fontSize: 21, lineHeight: 1.7, marginBottom: 24 }}>
            New York City has more than 8 million people and 1,064 public restrooms.
            Only 973 of them work. Paris has 400 for a quarter that population.
          </p>
          <p style={{ fontSize: 21, lineHeight: 1.7, color: C.soft }}>
            You know the drill. Buy a coffee you don't want. Beg a hostess for a
            code. Hold it. That's not a city problem — it's a dignity problem.
          </p>
        </section>

        {/* ===== WHAT WE'RE BUILDING ===== */}
        <section
          style={{
            backgroundColor: C.dark,
            color: C.cream,
            padding: "80px 24px",
          }}
        >
          <div style={{ maxWidth: 680, margin: "0 auto" }}>
            {sectionLabel("What We're Building")}
            <p
              style={{
                fontSize: 21,
                lineHeight: 1.7,
                marginBottom: 24,
                color: C.cream,
              }}
            >
              Twelve private rooms in SoHo. Hospital-grade clean. Circadian
              lighting that shifts with the time of day. A Toto Neorest in every
              room. Sonos audio. A locked door and ten minutes of peace.
            </p>
            <p
              style={{
                fontSize: 21,
                lineHeight: 1.7,
                marginBottom: 32,
                color: "color-mix(in srgb, var(--cream) 73%, transparent)",
              }}
            >
              And The Hull — a free communal space in the center of it all.
              Water, ice, phone charging, a living wall, and a seat. No purchase
              required. No questions asked.
            </p>
            <p
              style={{
                fontSize: 22,
                lineHeight: 1.6,
                fontStyle: "italic",
                color: C.gold,
              }}
            >
              The rooms are what keep the Hull free and open to everyone.
              That's the whole model.
            </p>
          </div>
        </section>

        <section style={{ maxWidth: 860, margin: '0 auto', padding: '56px 24px 0' }}>
          <div style={{ position: 'relative' }}>
            <img
              src="/room-build.webp"
              alt="Maslow room — concept render"
              width={2400}
              height={1808}
              loading="lazy"
              decoding="async"
              style={{ display: 'block', width: '100%', maxHeight: 520, objectFit: 'cover', objectPosition: 'center 60%' }}
            />
            <span
              style={{
                position: 'absolute',
                right: 16,
                bottom: 12,
                fontFamily: 'var(--sans)',
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'color-mix(in srgb, var(--cream) 70%, transparent)',
                pointerEvents: 'none',
              }}
            >
              Concept render
            </span>
          </div>
        </section>

        {/* ===== PASSES ===== */}
        <section
          style={{ padding: "80px 24px", maxWidth: 860, margin: "0 auto" }}
        >
          {sectionLabel("Reserve Your Place")}
          <h2
            style={{
              fontSize: "clamp(26px, 5vw, 38px)",
              fontWeight: 400,
              marginBottom: 12,
            }}
          >
            Passes
          </h2>
          <p
            style={{
              fontSize: 17,
              color: C.soft,
              marginBottom: 36,
              maxWidth: 480,
            }}
          >
            Pre-purchase visits. Use them when we open.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 16,
              marginBottom: 60,
            }}
          >
            {/* Ruled 9/17: the bundle leads. Same $5 a visit, and a $25 charge loses 4% to card fees
                where a $5 charge loses 9%. The single pass stays visible; it is the number on the sign. */}
            {tierCard({
              tierId: "starter_bundle",
              title: "Starter Bundle",
              price: "$25",
              sub: "5 visits · where most people start",
              desc: "Five visits, five dollars each. Enough to make it a habit.",
            })}
            {tierCard({
              tierId: "single_pass",
              title: "Single Pass",
              price: "$5",
              desc: "One visit. Walk in, lock the door, take ten minutes.",
            })}
          </div>

          {/* ===== BRICKS ===== */}
          <div
            style={{
              borderTop: `1px solid ${C.divider}`,
              paddingTop: 60,
              marginBottom: 60,
            }}
          >
            <h2
              style={{
                fontSize: "clamp(26px, 5vw, 38px)",
                fontWeight: 400,
                marginBottom: 12,
              }}
            >
              Maslow Bricks
            </h2>
            <p
              style={{
                fontSize: 17,
                lineHeight: 1.7,
                color: C.soft,
                marginBottom: 16,
                maxWidth: 560,
              }}
            >
              The Hull is the free heart of every Maslow. Built from reclaimed
              NYC brick with moss pressed into the joints. Now you can be part of
              it.
            </p>
            <p
              style={{
                fontFamily: 'var(--sans)',
                fontSize: 14,
                color: C.gray,
                marginBottom: 36,
              }}
            >
              Hull placements limited to 1,000.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 16,
              }}
            >
              {tierCard({
                tierId: "home_brick",
                title: "Home Brick",
                price: "$150",
                sub: "Includes 5 visits",
                desc: "Cast terrazzo — the Maslow logo on the face, your inscription on the back — shipped to your door during build-out. A piece of Maslow on your shelf.",
                highlight: true,
                selected: brickChoice === "home_brick",
              })}
              {tierCard({
                tierId: "hull_brick",
                title: "Hull Brick",
                price: "$150",
                sub: "Includes 5 visits",
                desc: "Your name engraved into the floor of The Hull — a permanent place in the room.",
                highlight: true,
                selected: brickChoice === "hull_brick",
              })}
              {tierCard({
                tierId: "both_bricks",
                title: "Both Bricks",
                price: "$250",
                sub: "Includes 10 visits — save $50",
                desc: "One for home, one in the Hull. The full set — a piece on your shelf, your name in the floor.",
                highlight: true,
                selected: brickChoice === "both_bricks",
              })}
            </div>
          </div>

          {/* ===== FOUNDING MEMBER ===== */}
          {FOUNDING_MEMBER_ON_MENU && (
          <div style={{ borderTop: `1px solid ${C.divider}`, paddingTop: 60 }}>
            <h2
              style={{
                fontSize: "clamp(26px, 5vw, 38px)",
                fontWeight: 400,
                marginBottom: 12,
              }}
            >
              Founding Member
            </h2>
            <div
              onClick={() => chooseTier("founding_member")}
              style={{
                border: `1px solid ${C.blue}`,
                borderRadius: 8,
                padding: 32,
                backgroundColor: "color-mix(in srgb, var(--blue) 2%, transparent)",
                maxWidth: 480,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <p
                style={{
                  fontSize: 42,
                  fontWeight: 600,
                  color: C.blue,
                  marginBottom: 8,
                  fontFamily: 'var(--serif)',
                }}
              >
                $500
                <span
                  style={{ fontSize: 18, fontWeight: 400, color: C.gray }}
                >
                  /year
                </span>
              </p>
              <p
                style={{
                  fontSize: 17,
                  lineHeight: 1.7,
                  color: C.soft,
                  marginBottom: 16,
                }}
              >
                Unlimited visits for a year. Priority booking. Guest passes.
                Both bricks included — one for home, your name in the Hull.
                The fullest way to reserve your place before the doors open.
              </p>
              <p
                style={{
                  fontFamily: 'var(--sans)',
                  fontSize: 12,
                  letterSpacing: 1.5,
                  textTransform: "uppercase",
                  color: C.gold,
                }}
              >
                Limited to 500
              </p>
            </div>
          </div>
          )}
        </section>

        {/* ===== DISCLOSURES — structure per counsel draft 4/14/26 ===== */}
        <section style={{ padding: "0 24px 80px", maxWidth: 680, margin: "0 auto" }}>
          {sectionLabel("Before you reserve")}
          <p style={{ fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.7, color: C.soft, marginBottom: 20 }}>
            <strong style={{ color: C.dark }}>Facility.</strong> Maslow's flagship location in SoHo, Manhattan is currently in development. All purchases — including visit passes, brick reservations, and founding memberships — are advance purchases of future access and products. Your passes will activate when our first location opens. Brick products will ship during the build-out period.
          </p>
          <p style={{ fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.7, color: C.soft, marginBottom: 20 }}>
            <strong style={{ color: C.dark }}>Timeline.</strong> We are targeting a 2028 opening for our SoHo flagship. Development timelines are subject to change based on permitting, construction, and lease finalization. We will keep all purchasers updated on progress via email.
          </p>
          <p style={{ fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.7, color: C.soft, marginBottom: 20 }}>
            <strong style={{ color: C.dark }}>Refunds.</strong> Sales are final, with one exception that matters: if Maslow has not opened a location within 36 months of your purchase date, you are entitled to a full refund of your purchase price. Email support@maslow.nyc with your order confirmation and it is processed within 10 business days. Bricks are made to order and cannot be refunded once your inscription is finalized. The{" "}
            <a href="/sales-agreement" style={{ color: C.blue }}>Sales Agreement</a> (sections 4 and 8) governs.
          </p>
          <p style={{ fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.7, color: C.soft }}>
            Every pass reserves access to a future Maslow service — not an investment or a donation.
          </p>
        </section>

        {/* ===== BY CONVERSATION (top tiers) ===== */}
        <section
          style={{
            backgroundColor: C.cream,
            padding: "80px 24px",
          }}
        >
          <div style={{ maxWidth: 860, margin: "0 auto" }}>
            {sectionLabel("By conversation")}
            <h2
              style={{
                fontSize: "clamp(26px, 5vw, 38px)",
                fontWeight: 400,
                marginBottom: 16,
                fontFamily: 'var(--serif)',
              }}
            >
              The Architect &amp; The Sovereign
            </h2>
            <p
              style={{
                fontSize: 17,
                lineHeight: 1.7,
                color: C.soft,
                maxWidth: 560,
                marginBottom: 24,
                fontFamily: 'var(--sans)',
              }}
            >
              Two founding tiers sit above everything on this page. They aren't
              listed here — they're a conversation. A private allocation, a
              permanent name on the founding plaque, and a literal key to the
              city.
            </p>
            <a
              href="mailto:hello@maslow.nyc?subject=Founding%20Allocation%20Inquiry"
              style={{
                fontSize: 15,
                fontFamily: 'var(--sans)',
                fontWeight: 600,
                color: C.gold,
                textDecoration: "none",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = C.goldHover;
                e.currentTarget.style.textDecoration = "underline";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.color = C.gold;
                e.currentTarget.style.textDecoration = "none";
              }}
            >
              Request an allocation
            </a>
          </div>
        </section>

        {/* ===== CTA ===== */}
        <section
          style={{
            backgroundColor: C.blue,
            padding: "80px 24px",
            textAlign: "center",
          }}
        >
          <div style={{ maxWidth: 480, margin: "0 auto" }}>
            <h2
              style={{
                fontSize: "clamp(26px, 5vw, 38px)",
                fontWeight: 400,
                color: "white",
                marginBottom: 16,
              }}
            >
              Be first in.
            </h2>
            <p
              style={{
                fontSize: 17,
                color: "color-mix(in srgb, var(--cream) 70%, transparent)",
                lineHeight: 1.65,
                marginBottom: 36,
              }}
            >
              We're building the list. Leave your email — you'll hear it here
              first.
            </p>
            {!submitted ? (
              <div>
                <div
                  style={{
                    display: "flex",
                    gap: 0,
                    maxWidth: 400,
                    margin: "0 auto",
                  }}
                >
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                    placeholder="you@email.com"
                    style={{
                      flex: 1,
                      padding: "14px 16px",
                      fontSize: 15,
                      fontFamily: 'var(--sans)',
                      border: "none",
                      borderRadius: "6px 0 0 6px",
                      outline: "none",
                      backgroundColor: "white",
                      color: C.dark,
                    }}
                  />
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    style={{
                      padding: "14px 20px",
                      fontSize: 13,
                      fontFamily: 'var(--sans)',
                      fontWeight: 600,
                      letterSpacing: 1,
                      textTransform: "uppercase",
                      backgroundColor: submitting ? C.goldHover : C.gold,
                      color: "white",
                      border: "none",
                      borderRadius: "0 6px 6px 0",
                      cursor: submitting ? "not-allowed" : "pointer",
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    {submitting ? "..." : "Count me in"}
                  </button>
                </div>
                {emailError && (
                  <p
                    style={{
                      color: "#FFB4B4",
                      fontSize: 13,
                      fontFamily: 'var(--sans)',
                      marginTop: 8,
                    }}
                  >
                    {emailError}
                  </p>
                )}
              </div>
            ) : (
              <p
                style={{
                  fontSize: 20,
                  color: "white",
                  fontStyle: "italic",
                }}
              >
                You're on the list. We'll be in touch.
              </p>
            )}
          </div>
        </section>

        {/* ===== FOOTER ===== */}
        <footer
          style={{
            padding: "36px 24px",
            textAlign: "center",
            backgroundColor: C.cream,
          }}
        >
          <p
            style={{
              fontFamily: 'var(--sans)',
              fontSize: 12,
              color: C.gray,
              marginBottom: 6,
            }}
          >
            Maslow LLC · support@maslow.nyc · maslow.nyc
          </p>
          <p style={{ fontSize: 15, fontStyle: "italic", color: C.gray }}>
            Premium is the floor, not an upgrade.
          </p>
        </footer>

        {/* Toast notification */}
        <Toast message={toast.message} visible={toast.visible} />
      </div>
    </>
  );
}
